import type { ApiError as ApiErrorBody } from '@edms/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Сессия — в httpOnly-cookie; токен в JS недоступен, поэтому его нельзя украсть через XSS. */
async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const res = await fetch(withQuery(`/api${path}`, query), {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const err = (data ?? {}) as Partial<ApiErrorBody>;
    if (res.status === 401 && path !== '/auth/login' && path !== '/auth/me') {
      window.dispatchEvent(new Event('edms:unauthorized'));
    }
    throw new ApiError(res.status, err.message ?? 'Не удалось выполнить запрос', err.fields ?? {});
  }
  return data as T;
}

/** Загрузка файла (multipart). Заголовок Content-Type с границей ставит браузер. */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`/api${path}`, { method: 'POST', credentials: 'same-origin', body: form });
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const err = (data ?? {}) as Partial<ApiErrorBody>;
    const message = res.status === 413 ? 'Файл слишком большой' : (err.message ?? 'Не удалось загрузить файл');
    throw new ApiError(res.status, message, err.fields ?? {});
  }
  return data as T;
}

/**
 * Загрузка с полосой: fetch не сообщает, сколько уже отправлено, поэтому XMLHttpRequest.
 * onProgress — доля от 0 до 1. Ошибки — как у upload.
 */
function uploadWithProgress<T>(path: string, form: FormData, onProgress: (share: number) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api${path}`);
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      const data = xhr.response as unknown;
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve(data as T);
        return;
      }
      const err = (data ?? {}) as Partial<ApiErrorBody>;
      if (xhr.status === 401) window.dispatchEvent(new Event('edms:unauthorized'));
      const message = xhr.status === 413 ? 'Файл слишком большой' : (err.message ?? 'Не удалось загрузить файл');
      reject(new ApiError(xhr.status, message, err.fields ?? {}));
    };
    xhr.onerror = () => reject(new ApiError(0, 'Нет связи с сервером — проверьте интернет и повторите'));
    xhr.send(form);
  });
}

export const api = {
  upload,
  uploadWithProgress,
  delete: <T>(path: string) => request<T>('DELETE', path),
  get: <T>(path: string, query?: Query) => request<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
};
